import {
  CompanyItem,
  DocumentLanguage,
  findDocumentLanguagesForOrgNo,
  getSigningMethodLabel,
  LocalizedText,
  SelectableWithId,
  SharepointSettingsForDocument,
  signer,
  Signer,
  SignerType,
  SigningMethod,
  signingMethodOptions,
} from "./common";
import { TMessage } from "../i18n/tt";
import { makeAutoObservable } from "mobx";
import { isValidEmail } from "../utils/email";
import { expandTimeString, isValidDate, isValidTime, toLocalDateISOString } from "../utils/Dates";
import { isBlank } from "../utils/strings";
import {
  DEFAULT_LANG,
  Language,
  languageLabels,
  supportedBackendLanguages,
} from "../i18n/languages";
import { compact, groupBy, identity, isFinite, partition, sortBy } from "lodash";
import { AIClient, AIModel } from "../ai/AIAssistant";
import { ArticlesOfAssociation, IArticle } from "../types/ArticlesOfAssociation";
import * as numberInput from "../utils/numberInput";
import { getFullName, isChairperson, validateName } from "../utils/Roles";
import { newUuid } from "../utils/uuid";
import { CompanyRole } from "../types/CompanyInfo";
import { toCompanyRoleReference } from "../utils/UrnReference";
import { RoleType } from "../types/enums/RoleType";
import { User } from "../types/UserInfo";
import { IconName } from "../components-v2/iconMapping";
import { IconProps } from "../components-v2/Icon";
import { LocalizedTextEditorModel } from "../text/localizedTextEditorDomain";
import { ReactElement } from "react";
import { logDebug } from "../utils/log";
import {
  SharepointContentInDrive,
  SharepointContext,
  SharepointDrive,
} from "../sharepoint/sharepointDomain";

type ChangeEvent = { target: HTMLInputElement | HTMLTextAreaElement };
export type StringFieldParams = {
  value?: string;
  required?: boolean;
  validateEmpty?: boolean;
  onChange?: (value: string | undefined) => void;
  validator?: (value: string) => TMessage | undefined;
};

export class StringFieldModel {
  isRequired: boolean;
  validatedValue: string;
  inputValue: string;
  baseValue: string;
  error?: TMessage = undefined;
  isDirty = false;

  private ref: HTMLInputElement | null | undefined;

  constructor(public params: StringFieldParams = {}) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.baseValue = params.value ?? "";
    this.isRequired = params.required ?? false;
    this.validatedValue = params.value ?? "";
    this.inputValue = params.value ?? "";
  }

  get isValid(): boolean {
    return !this.error;
  }

  get isChanged(): boolean {
    return this.inputValue.trim() !== this.baseValue.trim();
  }

  get isEmpty(): boolean {
    return isBlank(this.inputValue);
  }

  setRequired(required: boolean | undefined) {
    this.isRequired = Boolean(required);
  }

  setInputValue(inputValueOrEvent: string | ChangeEvent): void {
    this.isDirty = true;
    if (typeof inputValueOrEvent === "string") {
      this.inputValue = inputValueOrEvent;
    } else {
      this.inputValue = inputValueOrEvent.target.value;
      if (inputValueOrEvent.target !== document.activeElement) {
        this.commit();
      }
    }
  }

  setAndCommitInputValue(inputValueOrEvent: string | ChangeEvent): void {
    this.setInputValue(inputValueOrEvent);
    this.commit();
  }

  commit() {
    if (!this.isDirty) {
      return;
    }
    this.isDirty = false;
    this.inputValue = this.inputValue.trim();
    if (this.validate()) {
      this.validatedValue = this.inputValue;
      this.params.onChange?.(this.validatedValue);
    }
  }

  validate(): boolean {
    if (this.isRequired && this.isEmpty) {
      this.error = { key: "required" };
    } else if (this.params.validator && !this.isEmpty) {
      this.error = this.params.validator(this.inputValue);
    } else {
      this.error = undefined;
    }
    return this.isValid;
  }

  clear() {
    this.setAndCommitInputValue("");
    this.error = undefined;
  }

  setError(error?: TMessage) {
    this.error = error;
  }

  setRef(ref: HTMLInputElement | null | undefined) {
    this.ref = ref;
  }

  focus() {
    this.ref?.focus();
  }

  rebase() {
    this.commit();
    this.baseValue = this.validatedValue;
  }
}

export type NumberFieldParams = {
  value?: number;
  required?: boolean;
  isInteger?: boolean;
  isPositive?: boolean;
  isPositiveOrZero?: boolean;
  minDecimals?: number;
  maxDecimals?: number;
  disableFormatting?: boolean;
  locale?: Language;
  onChange?: (value: number | undefined) => void;
  validator?: (value: number | undefined) => TMessage | undefined;
};

export class NumberFieldModel {
  readonly params: NumberFieldParams;
  inputValue: string;
  error?: TMessage = undefined;
  isDirty = false;
  isRequired: boolean;
  locale: Language;
  ref: HTMLInputElement | null | undefined;

  constructor(params: NumberFieldParams = {}) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.locale = params.locale ?? DEFAULT_LANG;
    this.params = params;
    this.isRequired = Boolean(params.required);
    this.inputValue = this.formatValue(params.value, this.locale);
  }

  get isInteger(): boolean {
    return Boolean(this.params.isInteger);
  }

  get isPositive(): boolean {
    return Boolean(this.params.isPositive);
  }

  get isPositiveOrZero(): boolean {
    return Boolean(this.params.isPositiveOrZero);
  }

  get isValid(): boolean {
    return !this.error;
  }

  get isEmpty(): boolean {
    return isBlank(this.inputValue);
  }

  get isChanged(): boolean {
    return this.toNumber() !== this.params.value;
  }

  setRef(ref: HTMLInputElement | null | undefined) {
    this.ref = ref;
  }

  get parsedValue(): number | undefined {
    return numberInput.parseInputString(this.inputValue, this.locale);
  }

  validate(): boolean {
    const trimmed = this.inputValue.trim();
    if (this.isRequired && !trimmed) {
      this.error = { key: "required" };
    } else if (
      trimmed &&
      this.isPositive &&
      this.parsedValue !== undefined &&
      this.parsedValue <= 0
    ) {
      this.error = { key: "number-field.not-positive" };
    } else if (
      trimmed &&
      this.isPositiveOrZero &&
      this.parsedValue !== undefined &&
      this.parsedValue < 0
    ) {
      this.error = { key: "number-field.not-positive-or-zero" };
    } else if (trimmed && this.parsedValue === undefined) {
      this.error = { key: "number-field.not-a-number" };
    } else if (this.params.validator) {
      this.error = this.params.validator(this.parsedValue);
    } else {
      this.error = undefined;
    }
    return this.isValid;
  }

  setInputValue(valueOrEvent: string | number | undefined | ChangeEvent) {
    this.isDirty = true;
    switch (typeof valueOrEvent) {
      case "string":
        this.inputValue = this.filterInputString(valueOrEvent);
        break;
      case "number":
        this.inputValue = !isFinite(valueOrEvent)
          ? ""
          : numberInput.formatInputString(valueOrEvent, this.locale, this.params);
        break;
      case "undefined":
        this.inputValue = "";
        break;
      default:
        this.inputValue = this.filterInputString(valueOrEvent.target.value);
        if (valueOrEvent.target !== document.activeElement) {
          this.commit();
        }
        break;
    }
  }

  setAndCommitInputValue(valueOrEvent: string | number | undefined | ChangeEvent) {
    this.setInputValue(valueOrEvent);
    this.commit();
  }

  commit() {
    if (!this.isDirty) {
      return;
    }
    this.isDirty = false;
    if (this.validate()) {
      this.inputValue = this.formatValue(this.parsedValue, this.locale);
    }
    this.params.onChange?.(this.parsedValue);
  }

  toNumber(): number | undefined {
    return this.parsedValue;
  }

  toNumberOrZero(): number {
    return this.parsedValue || 0;
  }

  setLocale(locale: Language) {
    if (locale !== this.locale) {
      this.inputValue = this.formatValue(this.parsedValue, locale);
      this.locale = locale;
    }
  }

  formatValue(value: number | undefined, locale: Language) {
    return numberInput.formatInputString(value, locale, {
      isInteger: this.isInteger,
      useGrouping: !this.params.disableFormatting,
      minDecimals: this.params.minDecimals,
      maxDecimals: this.params.maxDecimals,
    });
  }

  filterInputString(value: string): string {
    return numberInput.filterInputString(value, this.locale, {
      maxDecimals: this.isInteger ? 0 : this.params.maxDecimals,
      useGrouping: !this.params.disableFormatting,
      isNegative: !this.isPositiveOrZero && !this.isPositive,
    });
  }

  setRequired(required: boolean | undefined) {
    this.isRequired = Boolean(required);
  }
}

export function toNumber(inputValue: string): number | undefined {
  const trimmedValue = inputValue.trim();
  if (trimmedValue && Number.isFinite(Number(trimmedValue))) {
    return Number(trimmedValue);
  }
}

export type BooleanFieldParams = {
  value?: boolean;
  required?: boolean;
  requiredTrue?: boolean;
  onChange?: (value: boolean) => void;
};

export class BooleanFieldModel {
  readonly params: BooleanFieldParams;

  value?: boolean;
  error?: TMessage = undefined;
  isRequired: boolean;
  isRequiredTrue: boolean;

  constructor(params: BooleanFieldParams = {}) {
    makeAutoObservable(this, { params: false }, { autoBind: true });
    this.params = params;
    this.value = params.value;
    this.isRequired = Boolean(params.required);
    this.isRequiredTrue = Boolean(params.requiredTrue);
  }

  get isValid(): boolean {
    return !this.error;
  }

  validate(): boolean {
    if ((this.isRequired && this.value === undefined) || (this.isRequiredTrue && !this.value)) {
      this.error = { key: "required" };
    } else {
      this.error = undefined;
    }
    return this.isValid;
  }

  setValue(value: string | undefined | boolean) {
    const oldValue = this.value;
    const newValue = value === "true" || value === true;
    if (newValue !== oldValue) {
      this.value = newValue;
      this.validate();
      this.params.onChange?.(newValue);
    }
  }

  toggle() {
    this.setValue(!this.value);
  }

  setRequired(required: boolean | undefined) {
    this.isRequired = Boolean(required);
  }
}

const yearPattern = /^\d{4}$/;

export function validateYearInput(
  value: string,
  params: { required?: boolean },
): TMessage | undefined {
  if (params.required && !value.trim()) {
    return { key: "required" };
  } else if (value.trim() && !yearPattern.test(value)) {
    return { key: "year-field.invalid-year" };
  }
}

export function validateLocalDateInput(
  value: string,
  params: { required?: boolean; minDate?: string; maxDate?: string },
): TMessage | undefined {
  if (params.required && !value.trim()) {
    return { key: "required" };
  } else if (value.trim() && !isValidDate(value)) {
    return { key: "localdate-field.invalid-date" };
  } else if (params.minDate && value < toLocalDateISOString(params.minDate)) {
    return {
      key: "dateTooEarly",
      params: { minDate: toLocalDateISOString(params.minDate) },
    };
  } else if (params.maxDate && value > toLocalDateISOString(params.maxDate)) {
    return {
      key: "dateTooLate",
      params: { maxDate: toLocalDateISOString(params.maxDate) },
    };
  }
}

export function validateTimeInput(
  value: string,
  params: { required?: boolean },
): TMessage | undefined {
  const trimmedValue = value.trim();
  if (params.required && !trimmedValue) {
    return { key: "required" };
  } else if (trimmedValue && !isValidTime(trimmedValue)) {
    return { key: "time-field.invalid-time" };
  }
}

type YearFieldParams = {
  value?: string;
  required?: boolean;
  onChange?: (value: string | undefined) => void;
};

export class YearFieldModel {
  readonly params: YearFieldParams;
  validatedValue: string | undefined;
  inputValue: string;
  error?: TMessage = undefined;
  isDirty = false;

  constructor(params: YearFieldParams = {}) {
    this.params = params;
    this.validatedValue = params.value;
    this.inputValue = params.value ?? "";
    makeAutoObservable(this, { params: false }, { autoBind: true });
  }

  setAndCommitInputValue(valueOrEvent: string | undefined | ChangeEvent) {
    this.setInputValue(valueOrEvent);
    this.commit();
  }

  setInputValue(valueOrEvent: string | number | undefined | ChangeEvent) {
    this.isDirty = true;
    switch (typeof valueOrEvent) {
      case "string":
        this.inputValue = valueOrEvent;
        break;
      case "number":
        this.inputValue = valueOrEvent.toString();
        break;
      case "undefined":
        this.inputValue = "";
        break;
      default:
        this.inputValue = valueOrEvent.target.value;
        if (valueOrEvent.target !== document.activeElement) {
          this.commit();
        }
        break;
    }
  }

  commit() {
    if (!this.isDirty) {
      return;
    }
    this.isDirty = false;
    this.inputValue = this.inputValue.trim();
    if (this.validate()) {
      this.validatedValue = this.inputValue || undefined;
      this.params.onChange?.(this.validatedValue);
    }
  }

  validate(): boolean {
    this.error = validateYearInput(this.inputValue, this.params);
    return this.isValid;
  }

  get isValid(): boolean {
    return !this.error;
  }

  toISOString(): string | undefined {
    return this.validatedValue;
  }

  toNumberOrZero() {
    return parseInt(this.validatedValue ?? "0");
  }
}

export type LocalDateFieldParams = {
  value?: string;
  required?: boolean;
  onChange?: (value: string | undefined) => void;
  minDate?: string;
  maxDate?: string;
};

export class LocalDateFieldModel {
  readonly params: LocalDateFieldParams;
  validatedValue: string | undefined;
  inputValue: string;
  error?: TMessage;
  isDirty = false;
  minDate?: string;
  maxDate?: string;
  isRequired: boolean;

  constructor(params: LocalDateFieldParams = {}) {
    this.params = params;
    this.validatedValue = params.value;
    this.inputValue = params.value ?? "";
    this.minDate = params.minDate;
    this.maxDate = params.maxDate;
    this.isRequired = Boolean(params.required);
    makeAutoObservable(this, { params: false }, { autoBind: true });
  }

  setInputValue(valueOrEvent: string | null | undefined | ChangeEvent) {
    this.isDirty = true;
    switch (typeof valueOrEvent) {
      case "string":
        this.inputValue = valueOrEvent;
        break;
      default:
        this.inputValue = valueOrEvent?.target.value ?? "";
        if (valueOrEvent && valueOrEvent.target !== document.activeElement) {
          this.commit();
        }
    }
  }

  setAndCommitInputValue(valueOrEvent: string | null | undefined | ChangeEvent) {
    this.setInputValue(valueOrEvent);
    this.commit();
  }

  commit() {
    if (!this.isDirty) {
      return;
    }
    this.isDirty = false;
    this.inputValue = this.inputValue.trim();
    if (this.validate()) {
      this.validatedValue = this.inputValue || undefined;
      this.params.onChange?.(this.validatedValue);
    }
  }

  validate(): boolean {
    this.error = validateLocalDateInput(this.inputValue, {
      required: this.isRequired,
      minDate: this.minDate,
      maxDate: this.maxDate,
    });
    return this.isValid;
  }

  get isValid(): boolean {
    return !this.error;
  }

  get isEmpty(): boolean {
    return !this.inputValue.trim();
  }

  get isChanged(): boolean {
    return this.inputValue.trim() !== (this.params.value?.trim() ?? "");
  }

  toISOString(): string | undefined {
    return this.validatedValue;
  }

  setRequired(required: boolean | undefined) {
    this.isRequired = Boolean(required);
  }
}

export type TimeFieldParams = {
  value?: string;
  required?: boolean;
  onChange?: (value: string | undefined) => void;
};

export class TimeFieldModel {
  readonly params: TimeFieldParams;
  validatedValue: string | undefined;
  inputValue: string;
  error?: TMessage;
  isDirty = false;

  constructor(params: TimeFieldParams = {}) {
    this.params = params;
    this.validatedValue = params.value;
    this.inputValue = params.value ?? "";
    makeAutoObservable(this, { params: false }, { autoBind: true });
  }

  get isValid(): boolean {
    return !this.error;
  }

  get isEmpty(): boolean {
    return !this.inputValue.trim();
  }

  setInputValue(valueOrEvent: string | undefined | ChangeEvent) {
    this.isDirty = true;
    switch (typeof valueOrEvent) {
      case "string":
        this.inputValue = valueOrEvent;
        break;
      case "undefined":
        this.inputValue = "";
        break;
      default:
        this.inputValue = valueOrEvent.target.value;
        if (valueOrEvent.target !== document.activeElement) {
          this.commit();
        }
        break;
    }
  }

  setAndCommitInputValue(valueOrEvent: string | undefined | ChangeEvent) {
    this.setInputValue(valueOrEvent);
    this.commit();
  }

  commit() {
    if (!this.isDirty) {
      return;
    }
    this.isDirty = false;
    this.expandValue();
    if (this.validate()) {
      this.validatedValue = this.inputValue || undefined;
      this.params.onChange?.(this.toISOString());
    }
  }

  expandValue() {
    this.inputValue = expandTimeString(this.inputValue);
  }

  validate(): boolean {
    this.error = validateTimeInput(this.inputValue, this.params);
    return this.isValid;
  }

  validateAfterTime(time: string) {
    if (this.validatedValue && this.validatedValue <= time) {
      this.error = { key: "timeTooEarly", params: { time: time } };
    }
  }

  toISOString(): string | undefined {
    return this.validatedValue;
  }

  validateBeforeTime(time: string) {
    if (this.validatedValue && this.validatedValue >= time) {
      this.error = { key: "timeTooLate", params: { time: time } };
    }
  }
}

type SelectableWithIdParams = {
  value?: Array<SelectableWithId>;
  required?: boolean;
  onChange?: () => void;
};

export class SelectableWithIdModel {
  readonly params: SelectableWithIdParams;

  value: Array<SelectableWithId>;
  error?: TMessage = undefined;

  constructor(params: SelectableWithIdParams = {}) {
    makeAutoObservable(this, { params: false }, { autoBind: true });
    this.params = params;
    this.value =
      params.value?.map((e) => ({
        id: e.id,
        name: e.name,
        isSelected: e?.isSelected !== undefined ? e.isSelected : true,
      })) ?? [];
  }

  get selectedCount(): number {
    return this.value.filter((it) => {
      return it.isSelected;
    }).length;
  }

  get selectedIds(): string[] {
    return this.value
      .filter((it) => {
        return it.isSelected;
      })
      .map((it) => it.id);
  }

  get isRequired(): boolean {
    return Boolean(this.params.required);
  }

  get isValid(): boolean {
    return !this.error;
  }

  validate(): boolean {
    if (this.params.required && this.value.length === 0) {
      this.error = { key: "required" };
    } else {
      this.error = undefined;
    }
    return this.isValid;
  }

  add(item: SelectableWithId) {
    if (!this.getById(item.id)) {
      this.value.push(item);
      this.params.onChange?.();
    }
  }

  remove(id: string) {
    const index = this.value.findIndex((x) => x.id === id);
    if (index !== -1) {
      this.value.splice(index, 1);
      this.params.onChange?.();
    }
  }

  isSelected(id: string): boolean {
    return this.getById(id)?.isSelected ?? false;
  }

  select(id: string) {
    const item = this.getById(id);
    if (item) {
      item.isSelected = true;
      this.params.onChange?.();
    }
  }

  setValue(value: Array<SelectableWithId>) {
    this.value = value;
  }

  selectAll() {
    this.value.forEach((value) => (value.isSelected = true));
    this.params.onChange?.();
  }

  unSelectAll() {
    this.value.forEach((value) => (value.isSelected = false));
    this.params.onChange?.();
  }

  unselect(id: string) {
    const item = this.getById(id);
    if (item) {
      item.isSelected = false;
      this.params.onChange?.();
    }
  }

  toggle(id: string) {
    if (this.isSelected(id)) {
      this.unselect(id);
    } else {
      this.select(id);
    }
  }

  getById(id: string): SelectableWithId | undefined {
    return this.value.find((x) => x.id === id);
  }

  replace(index: number, option: SelectableWithId) {
    this.value[index] = option;
    this.params.onChange?.();
  }
}

export type OptionFieldParams<T> = {
  value?: T[];
  selectedIds?: string[];
  options: T[] | (() => T[]);
  idFn: (value: T) => string;
  labelFn: (value: T) => TMessage | ReactElement;
  onChange?: () => void;
  required?: boolean;
  disabledFn?: (option: T) => boolean;
};

export class OptionFieldModel<T> {
  params: OptionFieldParams<T>;
  selectedIds: Set<string>;
  error?: TMessage = undefined;
  isRequired: boolean;

  constructor(params: OptionFieldParams<T>) {
    this.params = params;
    this.selectedIds = this.validIds(params.selectedIds ?? (params.value ?? []).map(params.idFn));
    this.isRequired = Boolean(params.required);
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isValid(): boolean {
    return !this.error;
  }

  validate(): boolean {
    if (this.isRequired && this.selectedIds.size < 1) {
      this.error = { key: "required" };
    } else {
      this.error = undefined;
    }
    return this.isValid;
  }

  get options(): T[] {
    if (typeof this.params.options === "function") {
      return this.params.options();
    }
    return this.params.options;
  }

  updateOptions(options: T[] | (() => T[])) {
    this.params.options = options;
  }

  get optionsById(): Map<string, T> {
    return new Map(this.options.map((o) => [this.getId(o), o]));
  }

  get selected(): T[] {
    return this.options.filter((o) => this.selectedIds.has(this.getId(o)));
  }

  get isAllSelected(): boolean {
    return this.options.every((o) => this.isSelected(o));
  }

  get isNoneSelected(): boolean {
    return this.selectedIds.size === 0;
  }

  isDisabled(option: T): boolean {
    return this.params.disabledFn?.(option) ?? false;
  }

  getId(v: T): string {
    return this.params.idFn(v);
  }

  getLabel(v: T): TMessage | ReactElement {
    return this.params.labelFn(v);
  }

  isSelected(value: T) {
    return this.selectedIds.has(this.getId(value));
  }

  toggle(option: T) {
    const id = this.getId(option);
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
    this.params.onChange?.();
    this.validate();
  }

  toggleAll(selected: boolean) {
    if (selected) {
      this.selectAll();
    } else {
      this.clear();
    }
    this.validate();
  }

  select(option: T) {
    const id = this.getId(option);
    if (!this.selectedIds.has(id)) {
      this.selectedIds.add(id);
      this.params.onChange?.();
    }
  }

  unselect(option: T) {
    if (this.selectedIds.delete(this.getId(option))) {
      this.params.onChange?.();
    }
  }

  selectAll() {
    this.selectedIds = new Set(this.optionsById.keys());
    this.params.onChange?.();
  }

  clear() {
    this.selectedIds = new Set();
    this.params.onChange?.();
  }

  private validIds(ids: string[]): Set<string> {
    const optionsById = this.optionsById;
    return new Set(ids.filter((id) => optionsById.has(id)));
  }

  setSelected(selected: T[]) {
    this.selectedIds = this.validIds(selected.map(this.getId));
    this.params.onChange?.();
  }
}

export type RadioFieldParams<T> = {
  value: T;
  options: T[] | (() => T[]);
  idFn: (option: T) => string;
  labelFn: (option: T) => TMessage;
  descriptionFn?: (option: T) => TMessage | ReactElement | string | number | undefined;
  disabledFn?: (option: T) => boolean;
  iconFn?: (option: T) => IconName | IconProps | undefined;
  onChange?: (option: T) => void;
  required?: boolean;
};

export class RadioFieldModel<T> {
  params: RadioFieldParams<T>;
  value: T;
  isRequired: boolean;
  error?: TMessage = undefined;

  constructor(params: RadioFieldParams<T>) {
    this.params = params;
    this.value = params.value;
    this.isRequired = Boolean(params.required);
    makeAutoObservable(this, {}, { autoBind: true });
  }

  getId(option: T): string {
    return option !== undefined ? this.params.idFn(option) : "";
  }

  getLabel(option: T): TMessage {
    return this.params.labelFn(option);
  }

  getDescription(option: T): TMessage | ReactElement | string | number | undefined {
    return this.params.descriptionFn?.(option);
  }

  isDisabled(option: T): boolean {
    return this.params.disabledFn?.(option) ?? false;
  }

  get options(): T[] {
    return Array.isArray(this.params.options) ? this.params.options : this.params.options();
  }

  updateOptions(options: T[] | (() => T[])) {
    this.params.options = options;
  }

  get isValid(): boolean {
    return !this.error;
  }

  get isChanged(): boolean {
    return this.params.idFn(this.value) !== this.params.idFn(this.params.value);
  }

  validate(): boolean {
    if (this.isRequired && (this.value === undefined || this.value === null)) {
      this.error = { key: "required" };
    } else {
      this.error = undefined;
    }
    return this.isValid;
  }

  setValue(value: T) {
    this.value = value;
    this.validate();
    this.params.onChange?.(value);
  }

  setRequired(required: boolean | undefined) {
    this.isRequired = Boolean(required);
  }
}

export const createEmailField = (params: StringFieldParams = {}) =>
  new StringFieldModel({
    ...params,
    validator: (s) => {
      if (params.validator) {
        const error = params.validator(s);
        if (error) {
          return error;
        }
      }
      if (!isValidEmail(s)) {
        return { key: "email-field.invalid-email" };
      }
      return undefined;
    },
  });

export class LocalizedStringFieldModel {
  model: StringFieldModel;
  locale: Language;
  aiAssistant?: AIModel;

  constructor(model: StringFieldModel, locale: Language, aiAssistant?: AIModel) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.model = model;
    this.locale = locale;
    this.aiAssistant = aiAssistant;
  }

  get language(): Language {
    return this.locale;
  }

  get text(): string {
    return this.model.validatedValue;
  }

  get isEmpty(): boolean {
    return this.model.isEmpty;
  }
}

export function updateLocalizedFields(
  documentLanguages: Language[],
  fields: LocalizedStringFieldModel[],
  initParams: StringFieldParams = {},
): LocalizedStringFieldModel[] {
  return documentLanguages.map(
    (language) =>
      fields.find((t) => t.locale === language) ??
      new LocalizedStringFieldModel(
        new StringFieldModel({
          ...initParams,
          value: fields.length > 0 ? fields[0]?.model.inputValue : undefined,
        }),
        language,
      ),
  );
}

export type DocumentLanguageFieldModel = RadioFieldModel<DocumentLanguage>;
export const requiredOfficialDocumentLanguageFieldModel = ({
  orgNo,
  value,
  onChange,
}: {
  orgNo?: string;
  value: DocumentLanguage;
  onChange?: (value: DocumentLanguage) => void;
}) => {
  const options = createLanguageOptions({ orgNo }).filter((o) => o !== "en");
  return documentLanguageFieldModel({
    orgNo,
    value,
    options,
    onChange,
  });
};

export const documentLanguageLabels: { [key in DocumentLanguage]: TMessage } = {
  no: { key: "language.no" },
  sv: { key: "language.sv" },
  en: { key: "language.en" },
  "no/en": { key: "language.no/en" },
  "sv/en": { key: "language.sv/en" },
};

export const createLanguageOptions = ({
  orgNo,
  options,
}: {
  orgNo?: string;
  options?: DocumentLanguage[];
}): DocumentLanguage[] => {
  if (!options) {
    return findDocumentLanguagesForOrgNo(orgNo);
  } else {
    return options;
  }
};
export const documentLanguageFieldModel = ({
  orgNo,
  value,
  options = createLanguageOptions({ orgNo }),
  onChange,
}: {
  orgNo?: string;
  value: DocumentLanguage;
  options?: DocumentLanguage[];
  onChange?: (value: DocumentLanguage) => void;
}): RadioFieldModel<DocumentLanguage> =>
  new RadioFieldModel<DocumentLanguage>({
    value,
    options,
    idFn: identity,
    labelFn: (l) => documentLanguageLabels[l],
    onChange,
  });
const defaultArticleLanguages = [Language.NO];

export class ArticleSelectorModel {
  articles?: ArticlesOfAssociation;
  selected?: number;

  constructor(articles?: ArticlesOfAssociation, selected?: number) {
    this.articles = articles;
    this.selected = selected;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get defaultLanguage(): Language {
    return this.articles?.languages[0] ?? Language.NO;
  }

  get articlesByParagraph(): Map<number, Map<Language, IArticle>> {
    return (this.articles?.jsondata ?? []).reduce((m, a) => {
      if (!m.has(a.paragraph)) {
        m.set(a.paragraph, new Map());
      }
      m.get(a.paragraph)!.set(a.language, a);
      return m;
    }, new Map<number, Map<Language, IArticle>>());
  }

  get options(): number[] {
    return Array.from(this.articlesByParagraph.keys()).sort((a, b) => a - b);
  }

  private getArticle(option: number, language: Language): IArticle | undefined {
    return (
      this.articlesByParagraph.get(option)?.get(language ?? this.defaultLanguage) ??
      this.articlesByParagraph.get(option)?.get(this.defaultLanguage)
    );
  }

  getHeading(option: number, language: Language): string {
    const article = this.getArticle(option, language);
    const prefix = this.articles?.paragraphFormat.prefix ?? "";
    const suffix = this.articles?.paragraphFormat.suffix ?? "";
    const name = article?.name ?? "";
    return `${prefix}${option} ${name}${suffix}`;
  }

  getText(option: number, language: Language): string {
    return this.getArticle(option, language)?.text ?? "";
  }

  setSelected(option?: number) {
    this.selected = option;
  }

  get isValid(): boolean {
    return this.selected !== undefined && this.options.includes(this.selected);
  }
}

export function initArticleTitleModels(params: {
  selectorModel: ArticleSelectorModel;
  value?: LocalizedText[];
}): LocalizedStringFieldModel[] {
  const selected = params.selectorModel.selected;
  if (!selected) {
    return [];
  }
  const languages = params.selectorModel.articles?.languages ?? defaultArticleLanguages;
  return languages.map((l) => {
    const article = params.selectorModel.articlesByParagraph.get(selected)?.get(l);
    const value = params.value?.find((t) => t.language === l);
    return new LocalizedStringFieldModel(
      new StringFieldModel({ value: value?.text ?? article?.name }),
      l,
    );
  });
}

export function initArticleTextModels(params: {
  selectorModel: ArticleSelectorModel;
  value?: LocalizedText[];
}): LocalizedStringFieldModel[] {
  const selected = params.selectorModel.selected;
  if (!selected) {
    return [];
  }
  const languages = params.selectorModel.articles?.languages ?? defaultArticleLanguages;
  return languages.map((l) => {
    const article = params.selectorModel.articlesByParagraph.get(selected)?.get(l);
    const value = params.value?.find((t) => t.language === l);
    return new LocalizedStringFieldModel(
      new StringFieldModel({ value: value?.text ?? article?.text }),
      l,
    );
  });
}

export function minLengthValidator(
  minLength: number,
  errorKey?: TMessage,
): (value: string) => TMessage | undefined {
  return (value) => {
    if (value.length < minLength) {
      return errorKey ?? { key: "minStringLength", params: { minLength } };
    }
  };
}

export class SignerModel {
  signer: Signer;
  nameField: StringFieldModel;
  emailField: StringFieldModel;
  methodField: RadioFieldModel<SigningMethod>;
  languageField: RadioFieldModel<Language>;

  constructor(signer: Signer) {
    this.signer = signer;
    this.nameField = new StringFieldModel({
      value: signer.name,
      required: true,
      validator: validateName,
    });
    this.emailField = createEmailField({ value: signer.email, required: true });
    this.methodField = new RadioFieldModel<SigningMethod>({
      value: signer.signingMethod,
      options: signingMethodOptions,
      idFn: identity,
      labelFn: getSigningMethodLabel,
    });
    this.languageField = new RadioFieldModel<Language>({
      value: signer.language,
      options: supportedBackendLanguages,
      idFn: identity,
      labelFn: (l) => ({ key: languageLabels[l] }),
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  get id(): string | number | undefined {
    return this.signer.id;
  }

  get key(): string | number {
    return this.signer.id ?? newUuid();
  }

  get signerType(): SignerType {
    return this.signer.signerType;
  }

  get isValid(): boolean {
    return this.nameField.isValid && this.emailField.isValid;
  }

  validate(): boolean {
    this.nameField.validate();
    this.emailField.validate();
    return this.isValid;
  }

  setSigningMethod(option: SigningMethod) {
    this.methodField.setValue(option);
  }

  toSigner(): Signer {
    return {
      ...this.signer,
      name: this.nameField.validatedValue,
      email: this.emailField.validatedValue ?? "",
      language: this.languageField.value,
      signingMethod: this.methodField.value,
    };
  }

  get isEmpty() {
    return this.nameField.isEmpty && this.emailField.isEmpty;
  }
}

export type SigningSettings = {
  sendSealedCopyTo: SendSealedCopyTo[];
  batchEmails: boolean;
};

export enum SendSealedCopyToType {
  SENDER = "SENDER",
  ALL_SIGNERS = "ALL_SIGNERS",
  OTHERS = "OTHERS",
}

export type SendSealedCopyTo = CopyToSender | CopyToAllSigners | CopyToOthers;
export type CopyToSender = { type: SendSealedCopyToType.SENDER };
export type CopyToAllSigners = { type: SendSealedCopyToType.ALL_SIGNERS };
export type CopyToOthers = {
  type: SendSealedCopyToType.OTHERS;
  recipients: SendSealedCopyToOther[];
};
export type SendSealedCopyToOther = {
  name: string;
  email: string;
};

export function mapSendSealedCopyTo(
  type: SendSealedCopyToType,
  recipients?: SendSealedCopyToOther[],
): SendSealedCopyTo {
  switch (type) {
    case SendSealedCopyToType.ALL_SIGNERS:
    case SendSealedCopyToType.SENDER:
      return { type };
    case SendSealedCopyToType.OTHERS:
      return { type, recipients: recipients ?? [] };
  }
}

export type QuickAddSignerItems = Array<{
  rowId: string;
  items: Array<{ id: string | number; label: TMessage; action: () => void }>;
}>;

export type CommonSigningProperties = {
  signingMethodFieldModel: RadioFieldModel<SigningMethod | null>;
  languageFieldModel: RadioFieldModel<Language>;
  changeCommonSigningMethod: () => void;
};

export type DocumentsForSigning = {
  documents: { id: string; name: string; reference?: string }[];
  selectedDocument?: { id: string; name: string; reference?: string };
  selectDocument: (document: { id: string; name: string; reference?: string }) => void;
  previousDocument?: () => void;
  nextDocument?: () => void;
};

export type CompaniesForSigning = {
  companies: CompanyItem[];
  selectedCompany?: CompanyItem;
  selectCompany: (company: CompanyItem) => void;
  previousCompany?: () => void;
  nextCompany?: () => void;
};

export type ShareholderItem = {
  id: string | number;
  name?: string;
  email?: string;
};

export class SendSealedCopyToOtherModel {
  id: string;
  name: StringFieldModel;
  email: StringFieldModel;

  constructor() {
    this.id = newUuid();
    this.name = new StringFieldModel({ required: true, validator: validateName });
    this.email = createEmailField({ required: true });
  }

  get isValid(): boolean {
    return this.name.isValid && this.email.isValid;
  }

  validate() {
    this.name.validate();
    this.email.validate();
    return this.isValid;
  }

  toSendSealedCopyToOther(): SendSealedCopyToOther {
    return {
      name: this.name.validatedValue,
      email: this.email.validatedValue,
    };
  }
}

export class SignersForm {
  items: SignerModel[];
  error?: TMessage;
  emailBody: LocalizedTextEditorModel;
  aiClient: AIClient;
  user: User | null;
  sendSealedCopyTo: OptionFieldModel<SendSealedCopyToType>;
  sendSealedCopyToOthers: SendSealedCopyToOtherModel[];
  shareholders?: ShareholderItem[];
  companyRoles?: CompanyRole[];
  signingMethodFieldModel: RadioFieldModel<SigningMethod | null>;
  batchEmails: RadioFieldModel<boolean>;
  showBatchEmailsSettings?: boolean;
  dismissedBatchEmailsInfo = false;

  emailTemplate: TMessage;

  selectSharepointDestinationDialog = false;
  sharepointContext?: SharepointContext;
  sharepointDrives: SharepointDrive[] = [];
  contentInSharepointDrive?: SharepointContentInDrive[];
  sharepointDrive: RadioFieldModel<SharepointDrive | undefined>;
  sharepointParent?: SharepointContentInDrive;
  seenSharepointFolders = new Set<SharepointContentInDrive>();
  isLoading = false;
  selectedSharepointDestination?: SharepointSettingsForDocument;

  constructor({
    signers,
    aiClient,
    user,
    fileName,
    shareholders,
    companyRoles,
    sendSealedCopyToDefaultSelection,
    showBatchEmailsSettings,
    sharepointContext,
    emailBody,
  }: {
    signers: Signer[];
    aiClient: AIClient;
    user: User | null;
    fileName?: string;
    shareholders?: ShareholderItem[];
    companyRoles?: CompanyRole[];
    sendSealedCopyToDefaultSelection?: SendSealedCopyToType[];
    showBatchEmailsSettings?: boolean;
    sharepointContext?: SharepointContext;
    emailBody?: TMessage;
  }) {
    this.shareholders = shareholders;
    this.companyRoles = companyRoles;
    this.items = signers.map((s) => new SignerModel(s));
    this.aiClient = aiClient;
    this.emailBody = new LocalizedTextEditorModel({
      texts: [{ text: "", language: Language.NO, translationOption: { autoTranslate: false } }],
      isReadOnly: () => false,
      label: { translated: "" },
      aiClient,
    });

    this.sendSealedCopyTo = new OptionFieldModel<SendSealedCopyToType>({
      value: sendSealedCopyToDefaultSelection ?? [
        SendSealedCopyToType.ALL_SIGNERS,
        SendSealedCopyToType.SENDER,
      ],
      idFn: (option) => String(option),
      options: Object.values(SendSealedCopyToType),
      labelFn: (option) => getSigningCompletionCopyToLabel(option, this.items),
    });
    this.sendSealedCopyToOthers = [new SendSealedCopyToOtherModel()];
    this.user = user;
    this.signingMethodFieldModel = new RadioFieldModel<SigningMethod | null>({
      value: this.selectedMethod(this.items[0]?.methodField?.value)
        ? this.items[0].methodField.value
        : null,
      options: signingMethodOptions,
      idFn: identity,
      labelFn: (option) => getSigningMethodLabel(option),
      onChange: (option) => this.toggleSigningMethod(option),
    });
    this.batchEmails = new RadioFieldModel<boolean>({
      value: showBatchEmailsSettings !== undefined && showBatchEmailsSettings,
      options: [true, false],
      idFn: String,
      labelFn: (v) =>
        v ? { key: "signing.batch-emails.enabled" } : { key: "signing.batch-emails.disabled" },
    });
    this.showBatchEmailsSettings = showBatchEmailsSettings;

    this.emailTemplate = emailBody ?? {
      key: "dbot.send-for-signing.email-body",
      params: { senderName: getFullName(this.user), fileName: fileName ?? "" },
    };
    this.sharepointContext = sharepointContext;
    this.selectedSharepointDestination = sharepointContext?.settings;
    this.sharepointDrive = new RadioFieldModel<SharepointDrive | undefined>({
      value: undefined,
      options: () => this.sharepointDrives,
      idFn: (option) => option?.id ?? "",
      labelFn: (option) => ({
        translated: option?.name ?? "",
      }),
      onChange: (option) => this.loadContentInSharepointDrive(option, undefined),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  init(text: string) {
    this.emailBody.textModels[0].model.setAndCommitInputValue(text);
  }

  setError(error?: TMessage) {
    this.error = error;
  }

  toSigningSettings(): SigningSettings {
    return {
      sendSealedCopyTo: this.sendSealedCopyTo.selected.map((d) =>
        mapSendSealedCopyTo(d, this.sendSealedCopyToOtherRecipients),
      ),
      batchEmails: this.batchEmails.value,
    };
  }

  get isSharepointEnabled(): boolean {
    return this.sharepointContext?.enabled ?? false;
  }

  toggleSelectSharepointDestinationDialog() {
    this.error = undefined;
    this.selectSharepointDestinationDialog = !this.selectSharepointDestinationDialog;
    if (this.selectSharepointDestinationDialog) {
      this.loadSharepointDrives();
    }
  }

  *loadSharepointDrives() {
    this.error = undefined;
    try {
      this.sharepointDrives = yield this.sharepointContext?.loadSharepointDrives?.();
    } catch (error) {
      logDebug("loadSharepointDestination", error);
      this.error = { key: "general-error" };
    }
  }

  *loadContentInSharepointDrive(
    drive: SharepointDrive | undefined,
    parent: SharepointContentInDrive | undefined,
  ) {
    this.error = undefined;
    this.contentInSharepointDrive = undefined;
    if (!drive) {
      return;
    }
    this.sharepointParent = parent;
    try {
      this.isLoading = true;
      this.contentInSharepointDrive = yield this.sharepointContext?.loadContentInSharepointDrive?.(
        drive.id,
        parent?.id,
      );
      this.contentInSharepointDrive?.forEach((e) => this.seenSharepointFolders.add(e));
      this.isLoading = false;
    } catch (error) {
      logDebug("loadSharepointDestination", error);
      this.error = { key: "general-error" };
      this.isLoading = false;
    }
  }

  *saveSharepointDestinationSettings() {
    try {
      this.isLoading = true;
      yield this.sharepointContext?.saveSharepointDestinationSettings(this.sharepointParent);
      this.isLoading = false;
      if (this.selectSharepointDestinationDialog) {
        this.toggleSelectSharepointDestinationDialog();
      }
    } catch (error) {
      logDebug("saveSharepointDestinationSettings", error);
      this.error = { key: "general-error" };
      this.isLoading = false;
    }
  }

  get selectSharepointLabel(): TMessage | undefined {
    if (this.sharepointParent) {
      return { key: "dbot.sharepoint.select-folder", params: { name: this.sharepointParent.name } };
    } else {
      return undefined;
    }
  }

  get onSelectSharepointDestination(): (() => void) | undefined {
    if (this.sharepointParent !== undefined) {
      return () => {
        this.saveSharepointDestinationSettings();
        this.selectedSharepointDestination = {
          documentType: this.sharepointContext!.documentType,
          destinationFolderId: this.sharepointParent!.id,
          driveId: this.sharepointDrive.value!.id,
          destinationFolderName: this.sharepointParent!.name,
        };
      };
    } else {
      return undefined;
    }
  }

  resetSharepointDestination(): void {
    this.sharepointParent = undefined;
    this.saveSharepointDestinationSettings();
    this.selectedSharepointDestination = undefined;
  }

  navigateToParent() {
    const grandParent = [...this.seenSharepointFolders].find(
      (i) => i.id === this.sharepointParent?.parentReference.id,
    );
    this.loadContentInSharepointDrive(this.sharepointDrive.value, grandParent);
  }

  get isSendSealedCopyToOthersSelected() {
    return this.sendSealedCopyTo.selected.includes(SendSealedCopyToType.OTHERS);
  }

  get sendSealedCopyToOtherRecipients() {
    return this.sendSealedCopyToOthers.map((d) => d.toSendSealedCopyToOther());
  }

  get enableRemoveOtherRecipients() {
    return this.sendSealedCopyToOthers.length > 1;
  }

  removeOtherRecipient(recipient: SendSealedCopyToOtherModel) {
    if (this.sendSealedCopyToOthers.includes(recipient)) {
      this.sendSealedCopyToOthers.splice(this.sendSealedCopyToOthers.indexOf(recipient), 1);
    }
    if (this.sendSealedCopyToOthers.length === 0) {
      this.addOtherRecipient();
    }
  }

  addOtherRecipient() {
    this.sendSealedCopyToOthers.push(new SendSealedCopyToOtherModel());
  }

  get groupedItems(): Array<[companyName: string, SignerModel[]]> {
    return Object.entries(groupBy(this.items, (item) => item.signer.companyName ?? "")).map(
      ([orgNo, items]) => [orgNo, sortBy(items, "signer.name")],
    );
  }

  showGroupHeading(companyName: string): boolean {
    return Boolean(companyName) && this.groupedItems.length > 1;
  }

  get hasChangedEmails(): boolean {
    return this.items.some((i) => i.emailField.isChanged);
  }

  get isValid(): boolean {
    const isSendSealedCopyToValid = this.isSendSealedCopyToOthersSelected
      ? this.sendSealedCopyToOthers.every((i) => i.isValid)
      : true;
    return this.items.length > 0 && this.items.every((i) => i.isValid) && isSendSealedCopyToValid;
  }

  get isSignerListEmpty(): boolean {
    return this.items.length === 0;
  }

  get isSubscriptionSignersPresent() {
    return this.items.some((signer) => signer.signerType === SignerType.SUBSCRIPTION);
  }

  validate(): boolean {
    this.items.forEach((item) => item.validate());
    if (this.isSendSealedCopyToOthersSelected) {
      this.sendSealedCopyToOthers.forEach((item) => item.validate());
    }
    return this.isValid;
  }

  toSigners(): Signer[] {
    return this.items.map((item) => item.toSigner());
  }

  getSignersWithChangedEmail(): Signer[] {
    return this.items.filter((item) => item.emailField.isChanged).map((item) => item.toSigner());
  }

  addSigner() {
    this.items.push(
      new SignerModel(signer({ signingMethod: this.signingMethodFieldModel.value ?? undefined })),
    );
  }

  toggleSigningMethod(method?: SigningMethod | null) {
    if (method) {
      this.items.forEach((m) => m.methodField.setValue(method));
    }
  }

  changeCommonSigningMethod() {
    const method = this.selectedMethod(this.items[0]?.methodField?.value)
      ? this.items[0].methodField.value
      : null;
    this.signingMethodFieldModel.setValue(method);
  }

  selectedMethod(selected?: SigningMethod): boolean {
    return selected !== undefined && this.items.every((m) => m.methodField.value === selected);
  }

  addRoleSigners(roles: CompanyRole[]) {
    if (
      this.items.length === 1 &&
      isBlank(this.items[0].nameField.inputValue) &&
      isBlank(this.items[0].emailField.inputValue)
    ) {
      this.items = [];
    }
    for (const role of roles) {
      const signer: Signer = {
        id: role.companyroleId,
        name: getFullName(role),
        email: role.email ?? "",
        signingMethod: this.signingMethodFieldModel.value ?? SigningMethod.BANKID_AUTH,
        signerType: SignerType.DOCUMENT,
        language: role.language ?? Language.NO,
        reference: toCompanyRoleReference(role.companyroleId),
      };
      if (!this.items.some((i) => i.id === role.companyroleId))
        this.items.push(new SignerModel(signer));
    }
  }

  addShareholdersAsSigners(shareholders: ShareholderItem[]) {
    if (
      this.items.length === 1 &&
      isBlank(this.items[0].nameField.inputValue) &&
      isBlank(this.items[0].emailField.inputValue)
    ) {
      this.items = [];
    }
    for (const shareholder of shareholders) {
      const shareholderAsSigner: Signer = {
        id: shareholder.id,
        name: shareholder.name ?? "",
        email: shareholder.email ?? "",
        signingMethod: SigningMethod.BANKID_AUTH,
        signerType: SignerType.DOCUMENT,
        language: Language.NO,
      };
      if (!this.items.some((i) => i.id === shareholder.id))
        this.items.push(new SignerModel(shareholderAsSigner));
    }
  }

  removeSigner(signer: SignerModel) {
    if (this.items.includes(signer)) {
      this.items.splice(this.items.indexOf(signer), 1);
    }
    if (this.items.length === 0) {
      this.addSigner();
    }
  }

  showButtonForRole(ceo: CompanyRole | undefined) {
    return !this.items.some((signer) => signer.id === ceo?.companyroleId);
  }

  get quickAddSignerItems(): QuickAddSignerItems | undefined {
    const [ceo, boardMembers] = partition(this.companyRoles, (r) => r.role === RoleType.CEO);
    const [chair, otherMembers] = partition(boardMembers, (r) => isChairperson(r.role));
    const shareholders = this.shareholders ?? [];

    return [
      {
        rowId: "named-people",
        items: compact([
          ceo.length === 1 &&
            this.showButtonForRole(ceo[0]) && {
              id: ceo[0].companyroleId,
              label: { key: "ceo-with-name", params: { name: getFullName(ceo[0]) } },
              action: () => this.addRoleSigners(ceo),
            },
          chair.length === 1 &&
            this.showButtonForRole(chair[0]) && {
              id: chair[0].companyroleId,
              label: { key: "chairperson-with-name", params: { name: getFullName(chair[0]) } },
              action: () => this.addRoleSigners(chair),
            },
          ...otherMembers
            .filter((m) => this.showButtonForRole(m))
            .map((m) => ({
              id: m.companyroleId,
              label: { translated: getFullName(m) },
              action: () => this.addRoleSigners([m]),
            })),
        ]),
      },
      {
        rowId: "groups",
        items: compact([
          boardMembers.length > 0 && {
            id: "boardMembers",
            label: {
              key: "addBoard",
              params: {
                n: boardMembers.length,
              },
            },
            action: () => this.addRoleSigners(boardMembers),
          },
          shareholders.length > 0 && {
            id: "shareholders",
            label: {
              key: "addShareholders",
              params: {
                n: shareholders.length,
              },
            },
            action: () => this.addShareholdersAsSigners(shareholders),
          },
        ]),
      },
    ];
  }

  commonSigningProperties(locale: Language): CommonSigningProperties {
    return {
      signingMethodFieldModel: this.signingMethodFieldModel,
      languageFieldModel: new RadioFieldModel<Language>({
        value: locale,
        options: supportedBackendLanguages,
        idFn: identity,
        labelFn: (l) => ({ key: languageLabels[l] }),
      }),
      changeCommonSigningMethod: this.changeCommonSigningMethod,
    };
  }

  dismissBatchEmailsInfo() {
    this.dismissedBatchEmailsInfo = true;
  }
}

export function getSigningCompletionCopyToLabel(
  recipient: SendSealedCopyToType,
  items: SignerModel[],
): TMessage {
  switch (recipient) {
    case SendSealedCopyToType.SENDER:
      return { key: "send-for-signing.send-to.myself" };
    case SendSealedCopyToType.ALL_SIGNERS:
      return {
        key: "send-for-signing.send-to.all",
        params: {
          n: items.length,
        },
      };
    case SendSealedCopyToType.OTHERS:
      return { key: "send-for-signing.send-to.other" };
  }
}
